/**
 * Headlight presentation styling for the Diagram / RichText editor input types.
 *
 * This is the WRAPPER chrome plus the small typography gaps PSF's view CSS leaves — the controls row,
 * the View/Edit segmented switch, rendered-markdown headings/lists, the single-segment editor, and the
 * print-theme border. The editors' own internals (Excalidraw, the markdown editor) self-register their
 * CSS; this only fills what the wrapper needs and what the app reset strips.
 *
 * NOTE: this whole module is a JS template literal — NEVER put a backtick in these comments, it
 * terminates the string and the module stops parsing (there is a test asserting no backticks).
 */
module.exports = /*css*/`
.pict-editor-controls
{
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 0.5rem;
	margin-bottom: 0.5rem;
}

/* is-small controls; align-items:center keeps them the same height, margin-bottom:0 undoes Bulma's
   .button spacing so the row height is exactly the button height. */
.pict-editor-controls .button
{
	margin-bottom: 0;
}

/* The View/Edit segmented switch. Both segments ship as is-link is-soft (soft, inactive look); the
   ACTIVE one is promoted to solid link here. The .button in the selector lifts specificity above
   Bulma's own .button.is-link.is-soft so the solid fill wins without !important. */
.pict-editor-switch
{
	margin-bottom: 0;
}
.pict-editor:not(.is-editing) .button.pict-editor-switch-view,
.pict-editor.is-editing .button.pict-editor-switch-edit
{
	background-color: var(--bulma-link, #485fc7);
	border-color: var(--bulma-link, #485fc7);
	color: var(--bulma-link-invert, #ffffff);
	z-index: 1;
}

/* --- Rendered-markdown typography (RichText view) ------------------------------------------------
   PSF's view CSS gives headings a color and margin but NO font-size, and does not touch lists at all,
   so the app's reset flattens h1..h6 to body size and strips bullets (verified: h1 rendered at 15px /
   weight 400, ul at list-style:none / padding:0). Restore a plain prose scale, scoped to the view. */
.pict-section-form-richtext-view h1 { font-size: 1.6em; font-weight: 700; }
.pict-section-form-richtext-view h2 { font-size: 1.35em; font-weight: 700; }
.pict-section-form-richtext-view h3 { font-size: 1.15em; font-weight: 700; }
.pict-section-form-richtext-view h4 { font-size: 1em; font-weight: 700; }
.pict-section-form-richtext-view h5 { font-size: 0.9em; font-weight: 700; }
.pict-section-form-richtext-view h6 { font-size: 0.85em; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; }
.pict-section-form-richtext-view ul,
.pict-section-form-richtext-view ol
{
	margin: 0.5em 0;
	padding-left: 1.5em;
}
.pict-section-form-richtext-view ul { list-style: disc outside; }
.pict-section-form-richtext-view ol { list-style: decimal outside; }
.pict-section-form-richtext-view li { margin: 0.15em 0; display: list-item; }

/* Single segment per field: PSF's provider only ever serialises one segment (its onContentChange
   writes the changed segment over the whole value), but the markdown editor still renders an
   "Add Segment" button. Hide it so the UI matches the model — extra segments only ever got dropped. */
.pict-section-form-richtext .pict-mde-add-segment
{
	display: none;
}

/* Remarks are text-only: PSF rejects the upload unless the descriptor sets RichText.AllowImages, and the
   hardened renderer drops the data: URI the editor would inline. Hide the Insert Image button to match. */
.pict-section-form-richtext .pict-mde-sidebar-btn-image
{
	display: none;
}

/* Print/PDF theme: drop the view's outline so the exported document reads as content, not a form
   field. Read keeps the border on purpose (an on-screen boundary). Scoped to the print container class
   the template adds only for the Print theme. */
.pict-editor-print .pict-section-form-diagram,
.pict-editor-print .pict-section-form-richtext
{
	border: 0;
	border-radius: 0;
}
`;
